import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { botonAzul, botonClaro, display } from '../asistencia/ui';
import { Ventana } from './ui';

const CONSULTA_MS = 800;
const PLAZO_MS = 150000;   // el lector espera un minuto por toque; con esto sobra

// Una huella de presilla: arcos anidados que bajan hasta el delta, donde las crestas van
// casi horizontales. Algunas se cortan (finales de cresta) para que no parezca hecha a
// compás. Se encienden de adentro hacia afuera según avanza el enrolamiento.
const FONDO = 40;
const CORTES = [null, null, [30, 5], null, [55, 6], [18, 5], [95, 6], [40, 5], null, [120, 6], [70, 6], [40, 5], [22, 6], [65, 5], null];
const CRESTAS = [
    ...Array.from({ length: 11 }, (_, i) => {
        const r = 3.6 + i * 4.1;
        const h = 5 + i * 4.7;
        const cx = 1.5 - i * 0.45;
        const y0 = 6 + i * 0.35;
        const izq = FONDO - (i % 3);
        const der = FONDO - ((i + 1) % 3);
        return `M${cx - r - 3} ${izq + 3} Q${cx - r} ${izq} ${cx - r} ${izq - 4} L${cx - r} ${y0} `
            + `A${r} ${h} 0 0 1 ${cx + r} ${y0} L${cx + r} ${der - 4} Q${cx + r} ${der} ${cx + r + 3} ${der + 3}`;
    }),
    ...Array.from({ length: 4 }, (_, j) => {
        const y = FONDO + 6 + j * 4.6;
        return `M-50 ${y + 3} Q0 ${y - 6} 50 ${y + 3}`;
    }),
];

const DibujoHuella = ({ encendidas, error, esperando }) => (
    <svg viewBox="-60 -72 120 144" className="w-40 h-48 overflow-visible" aria-hidden>
        <defs>
            <clipPath id="huella-silueta"><ellipse cx="0" cy="3" rx="46" ry="60" /></clipPath>
            <linearGradient id="huella-luz" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#22D3EE" stopOpacity="0" />
                <stop offset="0.5" stopColor="#22D3EE" stopOpacity="0.55" />
                <stop offset="1" stopColor="#22D3EE" stopOpacity="0" />
            </linearGradient>
        </defs>
        {esperando && <ellipse cx="0" cy="3" rx="54" ry="68" fill="none" stroke="#06B6D4" strokeWidth="1.2" className="huella-espera" />}
        <g clipPath="url(#huella-silueta)">
            {CRESTAS.map((d, i) => (
                <path
                    key={d} d={d} fill="none" strokeWidth="2.1" strokeLinecap="round"
                    strokeDasharray={CORTES[i] ? `${CORTES[i][0]} ${CORTES[i][1]} 999` : undefined}
                    className={`huella-cresta ${error ? 'error' : i < encendidas ? 'encendida' : ''}`}
                    style={{ transitionDelay: `${i * 45}ms` }}
                />
            ))}
            {esperando && <rect x="-60" y="-8" width="120" height="16" fill="url(#huella-luz)" className="huella-barrido" />}
        </g>
    </svg>
);

const PASOS = {
    primer_toque: { titulo: 'Pon el dedo en el lector', encendidas: 0 },
    segundo_toque: { titulo: 'Otra vez, para confirmar', encendidas: 6 },
    guardando: { titulo: 'Guardando la huella', encendidas: CRESTAS.length },
    lista: { titulo: 'Huella guardada', encendidas: CRESTAS.length },
    error: { titulo: 'No se pudo enrolar', encendidas: 0 },
};

/**
 * Sigue un enrolamiento: el backend tiene el lector esperando el dedo y aquí se va
 * mostrando cada paso. El lector es el de una puerta: la persona tiene que estar ahí.
 */
const Huella = ({ trabajoId, dedo, lector, onCerrar, onReintentar, onLista }) => {
    const [paso, setPaso] = useState({ estado: 'primer_toque', mensaje: null });
    const inicio = useRef(Date.now());
    const avisado = useRef(false);
    const alListo = useRef(onLista);
    alListo.current = onLista;

    useEffect(() => {
        let vigente = true;
        let t;
        const consultar = async () => {
            try {
                const { data } = await axiosInstance.get(`/api/puertas/huellas/${trabajoId}`);
                if (!vigente) return;
                setPaso(data);
                if (data.estado === 'lista' && !avisado.current) {
                    avisado.current = true;
                    alListo.current?.();
                }
                if (data.estado === 'lista' || data.estado === 'error') return;
            } catch (e) {
                if (!vigente) return;
                if (e.response?.status === 404) {
                    setPaso({ estado: 'error', mensaje: 'Se perdió el enrolamiento (el servidor se reinició). Inténtalo de nuevo.' });
                    return;
                }
            }
            if (Date.now() - inicio.current > PLAZO_MS) {
                setPaso({ estado: 'error', mensaje: 'El lector no respondió a tiempo.' });
                return;
            }
            t = setTimeout(consultar, CONSULTA_MS);
        };
        consultar();
        return () => { vigente = false; clearTimeout(t); };
    }, [trabajoId]);

    const p = PASOS[paso.estado] || PASOS.primer_toque;
    const esperando = paso.estado === 'primer_toque' || paso.estado === 'segundo_toque';
    const terminado = paso.estado === 'lista' || paso.estado === 'error';

    return (
        <Ventana titulo="Enrolar huella" onCerrar={onCerrar} ancho="max-w-sm">
            <div className="mt-5 flex flex-col items-center text-center" aria-live="polite">
                <DibujoHuella encendidas={p.encendidas} error={paso.estado === 'error'} esperando={esperando} />
                <p className={`${display} mt-5 text-lg font-bold ${paso.estado === 'error' ? 'text-rose-600' : 'text-slate-900'}`}>
                    {paso.estado === 'lista' && <Check size={18} className="inline -mt-1 mr-1.5 text-cyan-600" />}
                    {p.titulo}
                </p>
                <p className="text-sm mt-1.5 text-slate-500">
                    {paso.estado === 'error' ? paso.mensaje : `${dedo}, en el lector de ${lector.puerta} (${lector.lado}).`}
                </p>
                {esperando && (
                    <p className="text-xs mt-3 text-slate-400">
                        Apoya el dedo plano, sin moverlo, hasta que el lector suene. Si cierras esto, el lector sigue esperando un minuto.
                    </p>
                )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
                {paso.estado === 'error' && <button type="button" onClick={onReintentar} className={botonClaro}>Intentar de nuevo</button>}
                {terminado
                    ? <button type="button" onClick={onCerrar} className={botonAzul}>{paso.estado === 'lista' ? 'Listo' : 'Cerrar'}</button>
                    : <button type="button" onClick={onCerrar} className={botonClaro}>Cerrar</button>}
            </div>
        </Ventana>
    );
};

export default Huella;
